import apiClient, { unwrapData } from "./apiClient";

function buildTranscript(detail = {}) {
  if (detail.transcript) {
    return detail.transcript;
  }

  const segmentLines = (detail.transcriptions?.items || [])
    .flatMap((item) =>
      (item.segments || []).map(
        (segment) => `${segment.speaker || "Speaker"}: ${segment.content || ""}`,
      ),
    )
    .filter(Boolean);

  if (segmentLines.length) {
    return segmentLines.join("\n");
  }

  const transcriptionText = (detail.transcriptions?.items || [])
    .map((item) => item.transcription)
    .filter(Boolean)
    .join("\n\n");

  return transcriptionText;
}

export async function getCalls(params = {}, options = {}) {
  const response = await apiClient.get("/api/calls", {
    ...options,
    query: {
      view: params.view,
      page: params.page,
      limit: params.limit,
      search: params.search,
      status: params.status,
      direction: params.direction,
      channel_type: params.channel_type,
      client_id: params.client_id,
      campaign_id: params.campaign_id,
      contact_id: params.contact_id,
      duration_min: params.duration_min,
      duration_max: params.duration_max,
      line: params.line,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  });
  const data = unwrapData(response) || {};

  return {
    items: Array.isArray(data.items) ? data.items : [],
    summary: data.summary || {},
    meta: response?.meta || {},
  };
}

export async function getCallHistory(params = {}, options = {}) {
  const response = await apiClient.get("/api/calls/history", {
    ...options,
    query: {
      page: params.page,
      limit: params.limit,
      search: params.search,
      status: params.status,
      script_status: params.script_status,
      direction: params.direction,
      channel_type: params.channel_type,
      client_id: params.client_id,
      campaign_id: params.campaign_id,
      contact_id: params.contact_id,
      duration_min: params.duration_min,
      duration_max: params.duration_max,
      line: params.line,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  });
  const data = unwrapData(response) || {};

  return {
    items: Array.isArray(data.items) ? data.items : [],
    summary: data.summary || {},
    meta: response?.meta || {},
  };
}

export async function getCallStats(params = {}, options = {}) {
  const response = await apiClient.get("/api/calls/stats", {
    ...options,
    query: {
      search: params.search,
      status: params.status,
      direction: params.direction,
      channel_type: params.channel_type,
      client_id: params.client_id,
      campaign_id: params.campaign_id,
      contact_id: params.contact_id,
      duration_min: params.duration_min,
      duration_max: params.duration_max,
      line: params.line,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  });

  return unwrapData(response) || {};
}

export async function getCallLiveSummary(params = {}, options = {}) {
  const response = await apiClient.get("/api/calls/live", {
    ...options,
    query: {
      search: params.search,
      direction: params.direction,
      channel_type: params.channel_type,
      client_id: params.client_id,
      campaign_id: params.campaign_id,
      contact_id: params.contact_id,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  });

  return unwrapData(response) || {};
}

export async function getCallQueue(options = {}) {
  const response = await apiClient.get("/api/calls/queue", options);
  const data = unwrapData(response);

  if (Array.isArray(data)) {
    return data;
  }

  return Array.isArray(data?.items) ? data.items : [];
}

export async function getCallingAgentSummary(options = {}) {
  const response = await apiClient.get("/api/calling-agent/summary", options);
  const data = unwrapData(response) || {};

  return {
    counts: data.counts || {},
    pending_calls: Array.isArray(data.pending_calls) ? data.pending_calls : [],
    assigned_calls: Array.isArray(data.assigned_calls) ? data.assigned_calls : [],
    completed_followups: Array.isArray(data.completed_followups)
      ? data.completed_followups
      : [],
  };
}

export async function getAssignedCalls(options = {}) {
  const response = await apiClient.get("/api/calls/assigned", options);
  const data = unwrapData(response);

  return Array.isArray(data) ? data : [];
}

export async function getAssignableCallingAgents(options = {}) {
  const response = await apiClient.get("/api/agents/calling-agents", options);
  const data = unwrapData(response);

  return Array.isArray(data) ? data : [];
}

export async function getCallDetail(id, options = {}) {
  const response = await apiClient.get(`/api/calls/${encodeURIComponent(id)}`, options);
  const detail = unwrapData(response) || {};

  return {
    ...detail,
    phone_number: detail.contact?.phone || detail.phone_number || "",
    caller_name: detail.contact?.name || detail.caller_name || "",
    call_direction: detail.call_direction || detail.direction || "outbound",
    summary: detail.summary?.summary || detail.summary || "",
    transcript: buildTranscript(detail),
  };
}

export async function assignCall(callId, payload = {}, options = {}) {
  const response = await apiClient.post(`/api/calls/${encodeURIComponent(callId)}/assign`, {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function assignCallsBulk(payload = {}, options = {}) {
  const response = await apiClient.patch("/api/calls/assign-bulk", {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function assignCallHistory(callLogId, payload = {}, options = {}) {
  const response = await apiClient.post(
    `/api/calls/history/${encodeURIComponent(callLogId)}/assign`,
    {
      ...options,
      body: payload,
    },
  );

  return unwrapData(response);
}

export async function assignCallHistoryBulk(payload = {}, options = {}) {
  const response = await apiClient.post("/api/calls/history/assign-bulk", {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export const getCall = getCallDetail;

export async function startCall(payload = {}, options = {}) {
  const response = await apiClient.post("/api/calls/start", {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function bulkStartCalls(payload = {}, options = {}) {
  const response = await apiClient.post("/api/calls/bulk-start", {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function cancelCall(callId, payload = {}, options = {}) {
  const response = await apiClient.post(`/api/calls/${encodeURIComponent(callId)}/cancel`, {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function retryCall(callId, payload = {}, options = {}) {
  const response = await apiClient.post(`/api/calls/${encodeURIComponent(callId)}/retry`, {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}

export async function getCallEvents(callId, options = {}) {
  const response = await apiClient.get(`/api/calls/${encodeURIComponent(callId)}/events`, options);

  return unwrapData(response);
}

export async function updateCallWorkflowStatus(callId, payload = {}, options = {}) {
  const response = await apiClient.patch(`/api/calls/${encodeURIComponent(callId)}/status`, {
    ...options,
    body: payload,
  });

  return unwrapData(response);
}
